export type TextAppearance = 'field' | 'inline';
export type TextOutline = 'focus' | 'none';

const FIELD =
  'border border-border-strong bg-surface focus:border-border-strongest focus:ring-1 focus:ring-border-strongest';
const INLINE = [
  'mx-[calc(-4*var(--ac-u))] w-[calc(100%+8*var(--ac-u))] px-[calc(4*var(--ac-u))] border-0 bg-transparent shadow-none',
  'hover:bg-surface-hover',
  'transition-[background-color,box-shadow] ease-[cubic-bezier(.2,.8,.2,1)]',
  'duration-[120ms] focus:duration-[80ms] motion-reduce:duration-[60ms] motion-reduce:focus:duration-[40ms]',
].join(' ');
const INLINE_FOCUS_RING =
  'focus:bg-transparent focus:ring-1 focus:ring-border-strongest';
// The one focus mark an outline-less field keeps for keyboard users: a background, never a border
const INLINE_FOCUS_TINT = 'focus:bg-surface-hover';

export const TEXT_APPEARANCE_CLASS: Record<TextAppearance, string> = {
  field: FIELD,
  inline: `${INLINE} ${INLINE_FOCUS_RING}`,
};

// Inline fields stay bare text while edited unless `focus` asks for the ring; the field appearance keeps its box
export const textAppearanceClass = (
  appearance: TextAppearance,
  outline: TextOutline | undefined
): string =>
  appearance === 'inline' && outline !== 'focus'
    ? `${INLINE} ${INLINE_FOCUS_TINT}`
    : TEXT_APPEARANCE_CLASS[appearance];

// The invalid state in the text-region table: a 1px danger border, no fill
export const FIELD_ERROR_BORDER_CLASS = 'ring-1 ring-inset ring-danger-8';

// An inline field's problem is a line under it; a boxed or ringed field keeps its ring instead
export const FIELD_ERROR_UNDERLINE_CLASS =
  'shadow-[inset_0_-2px_0_theme(colors.danger.8)]';

export const fieldErrorClass = (
  appearance: TextAppearance,
  outline: TextOutline | undefined
): string =>
  appearance === 'field' || outline === 'focus'
    ? FIELD_ERROR_BORDER_CLASS
    : FIELD_ERROR_UNDERLINE_CLASS;

// Muted danger for an empty field's placeholder while it has an error, applied by
// whichever element draws it (a native placeholder, a text span, the rich editor's
// empty paragraph)
export const FIELD_ERROR_PLACEHOLDER_CLASS = {
  native: 'placeholder:text-danger-11/60',
  text: 'text-danger-11/60',
  editor: '[&_.is-empty]:before:!text-danger-11/60',
} as const;
